/**
 * One reader for every phone-gate refusal a web client can receive.
 *
 * The gate blocks outbound contact until the account verifies a phone number,
 * and the verify page is the only next step a refused user has. Each surface
 * answers in the shape its own endpoint family uses: the legacy `/app` writers
 * send `{error, redirect}`, and `/v1` sends its error envelope carrying the
 * code below. Both mean the same thing, so both lead to the same page.
 *
 * A refusal is not a send failure. A client that renders it as one leaves the
 * user on a screen that names no way forward, which is the whole defect this
 * module exists to close.
 */

export const PHONE_VERIFICATION_REQUIRED = 'phone_verification_required';

// Non-local coupling: LinksHelper::getVerifyPhone() owns this path.
const VERIFY_PHONE_PATH = '/app/getting-started/verify-phone';

// Only a single-slash relative path is followed, so a reflected value cannot
// drive the browser to another origin. The second character must not be "/" or
// "\": browsers normalize "\" to "/", so "/\host" would resolve to "//host".
// Same rule the global ajaxComplete handler applies in jq-common.js.
const SAME_ORIGIN_PATH = /^\/(?![/\\])/;

// The URL parser drops tab, newline and carriage return before it resolves,
// so "/\t/host" reaches another origin while passing the test above.
const STRIPPED_BY_THE_URL_PARSER = /[\t\n\r]/;

function sameOriginPath(target) {
    return typeof target === 'string'
        && !STRIPPED_BY_THE_URL_PARSER.test(target)
        && SAME_ORIGIN_PATH.test(target);
}

function goToVerifyPhone(target) {
    window.location.assign(sameOriginPath(target) ? target : VERIFY_PHONE_PATH);
}

/**
 * Navigate when a REST reply refuses on the phone gate.
 *
 * The body is read from a clone, so a caller that parses the same response for
 * its own reasons still gets an unconsumed stream.
 *
 * @param {Response} response
 * @returns {Promise<boolean>} whether the refusal was recognised
 */
export async function followApiPhoneGateRefusal(response) {
    if (!response || response.status !== 403) return false;

    let body = null;
    try {
        body = await response.clone().json();
    } catch (e) { // eslint-disable-line no-unused-vars
        return false;
    }

    if (!body || !body.error || body.error.code !== PHONE_VERIFICATION_REQUIRED) return false;

    goToVerifyPhone(null);

    return true;
}

/**
 * Navigate when a legacy `/app` JSON reply refuses on the phone gate.
 *
 * @param {*} body the parsed reply, or null when it did not parse
 * @returns {boolean} whether the refusal was recognised
 */
export function followWebPhoneGateRefusal(body) {
    if (!body || body.error !== PHONE_VERIFICATION_REQUIRED) return false;

    goToVerifyPhone(body.redirect);

    return true;
}

/**
 * Read a jQuery error argument as JSON.
 *
 * jQuery fills `responseJSON` only for a request that declared or sniffed the
 * JSON data type, and the comment form declares none, so the text is parsed
 * when the field is absent.
 *
 * @param {*} xhr
 * @returns {*} the parsed body, or null
 */
export function phoneGateRefusalBody(xhr) {
    if (!xhr) return null;
    if (xhr.responseJSON) return xhr.responseJSON;

    try {
        return JSON.parse(xhr.responseText);
    } catch (e) { // eslint-disable-line no-unused-vars
        return null;
    }
}

if (typeof window !== 'undefined') {
    // The PM and comment clients are concatenated into the bundle rather than
    // imported, so they reach these through the window.
    window.PhoneGateRefusal = {
        followApi: followApiPhoneGateRefusal,
        followWeb: followWebPhoneGateRefusal,
        refusalBody: phoneGateRefusalBody,
    };
}
